// State machine for the dev-mode playmat-component placement
// editor. Mirrors GridEditor's shape but operates on a fixed catalog
// of 12 placements (6 components × 2 players). Each placement
// carries a world-coord center {x, z} and a player flag (the player
// flag drives the 180° rotation for P2 components. The data only
// stores position, the renderer applies the rotation).
//
// No mode toggles for "drag-start" / "drag-end." The editor opens
// in adjust mode with every component visible on the mat at its
// current position. The user clicks any component to start dragging
// it. Releasing commits the new position.
//
// `onChange(placements)` fires after every drag so the host can
// re-render the 3D meshes and (eventually) the procedural texture.

// Catalog of the 12 placeable components. Shared schema used by the
// editor (drag UI) and the generator (which composites these into
// the UI overlay texture at render time). `player` drives the 180°
// rotation: 1 = upright as P1 sees it, 2 = rotated so P2 sees it
// upright. `toggleKey` (when set) lets the player turn the component
// off via their per-player config. Unset = always rendered.
export const COMPONENT_KEYS = [
  { key: 'atlas',      file: 'atlas.svg',            player: 1, label: 'Atlas (P1)' },
  { key: 'atlas2',     file: 'atlas.svg',            player: 2, label: 'Atlas (P2)' },
  { key: 'spellbook',  file: 'spellbook.svg',        player: 1, label: 'Spellbook (P1)' },
  { key: 'spellbook2', file: 'spellbook.svg',        player: 2, label: 'Spellbook (P2)' },
  { key: 'cemetery',   file: 'cemetery.svg',         player: 1, label: 'Cemetery (P1)' },
  { key: 'cemetery2',  file: 'cemetery.svg',         player: 2, label: 'Cemetery (P2)' },
  // Life + mana trackers retired from the procedural playmat UI:
  // the game tracks both via in-scene DOM widgets instead, and
  // doubling the tracker rails on the mat-art layer was redundant.
  // Entries kept here (so the SVG assets, asset-loader, and
  // placement coords in COMPONENT_PLACEMENTS don't bit-rot) but
  // `disabled: true` skips them in the generator + editor.
  { key: 'lifeP1',     file: 'life-tracker.svg',     player: 1, label: 'Life Tracker (P1)',     toggleKey: 'showLifeTracker', disabled: true },
  { key: 'lifeP2',     file: 'life-tracker.svg',     player: 2, label: 'Life Tracker (P2)',     toggleKey: 'showLifeTracker', disabled: true },
  { key: 'manaP1',     file: 'mana-tracker.svg',     player: 1, label: 'Mana Tracker (P1)',     toggleKey: 'showManaTracker', disabled: true },
  { key: 'manaP2',     file: 'mana-tracker.svg',     player: 2, label: 'Mana Tracker (P2)',     toggleKey: 'showManaTracker', disabled: true },
  // Affinity tracker SVGs retired from the procedural playmat UI
  // alongside life + mana. The DOM HUD owns the live counter
  // display now and the on-mat rail was redundant. `disabled: true`
  // hides them in both the generator (no bake) and the editor
  // (not draggable). Coords in COMPONENT_PLACEMENTS, the SVG asset,
  // and the AssertionRenderer code stay intact for a one-line
  // re-enable if needed.
  { key: 'affinityP1', file: 'affinity-tracker.svg', player: 1, label: 'Affinity Tracker (P1)', toggleKey: 'showAffinityTracker', disabled: true },
  { key: 'affinityP2', file: 'affinity-tracker.svg', player: 2, label: 'Affinity Tracker (P2)', toggleKey: 'showAffinityTracker', disabled: true },
];

export class PlaymatComponentEditor {
  constructor({ renderer, getInitialPlacements, onChange }) {
    this.renderer = renderer;
    this.getInitialPlacements = getInitialPlacements || (() => ({}));
    this.onChange = onChange || (() => {});
    this.active = false;
    this.placements = {};
    this.draggingKey = null;
    this.dragOffset = null;
  }

  isActive() { return this.active; }

  start() {
    this.placements = { ...(this.getInitialPlacements() || {}) };
    this.active = true;
    this.renderer.update(this.placements, this.draggingKey);
    this.onChange(this.placements);
  }

  stop() {
    this.active = false;
    this.placements = {};
    this.draggingKey = null;
    this.dragOffset = null;
    this.renderer.clear();
  }

  getPlacements() {
    // Return a shallow clone so callers can't mutate our state.
    const out = {};
    for (const [k, v] of Object.entries(this.placements)) out[k] = { ...v };
    return out;
  }

  // worldPoint = {x, z} from raycastTablePoint
  // pickedMesh = the THREE.Mesh under the cursor, if any
  pointerDown(worldPoint, pickedMesh) {
    if (!worldPoint || !pickedMesh?.userData?.componentKey) return false;
    const key = pickedMesh.userData.componentKey;
    const placement = this.placements[key];
    if (!placement) return false;
    this.draggingKey = key;
    this.dragOffset = {
      dx: worldPoint.x - placement.x,
      dz: worldPoint.z - placement.z,
    };
    this.renderer.update(this.placements, this.draggingKey);
    return true;
  }

  pointerMove(worldPoint) {
    if (!this.draggingKey || !worldPoint) return false;
    const placement = this.placements[this.draggingKey];
    if (!placement) return false;
    this.placements[this.draggingKey] = {
      ...placement,
      x: worldPoint.x - this.dragOffset.dx,
      z: worldPoint.z - this.dragOffset.dz,
    };
    this.renderer.update(this.placements, this.draggingKey);
    this.onChange(this.placements);
    return true;
  }

  pointerUp() {
    if (!this.draggingKey) return false;
    this.draggingKey = null;
    this.dragOffset = null;
    this.renderer.update(this.placements, this.draggingKey);
    return true;
  }
}
